import {
  apAnalytics,
  registerApEvents,
  takeApHandoff,
  type ApContext
} from '../apAnalytics'

export const ISSUE_SCREEN = 'card_issuance'
// The cards list calls observeIssueDestination when it becomes usable.
export const ISSUE_DESTINATION_HANDOFF = 'ap-cards-issue-destination'

const cardStatuses = [
  'pending_funding',
  'issued',
  'authorized',
  'settled',
  'blocked',
  'cancelled',
  'expired',
  'unknown'
] as const

registerApEvents({
  events: {
    'apCards.issue.screenViewed':
      'issuanceIntentId visitType entrySource formRevisionId usageType availableControlIds fundingState eligibleFundingAccountCount selectedFundingAccountId cardholderLoadState loadDurationMs',
    'apCards.issue.fieldInteracted':
      'issuanceIntentId fieldId interaction hasValue validationState formRevisionId',
    'apCards.issue.usageChanged':
      'issuanceIntentId previousUsageType usageType formRevisionId',
    'apCards.issue.cardholderMenuChanged':
      'issuanceIntentId state reason loadedCount complete availableOptionCount selectedCardholderId selectionKind',
    'apCards.issue.cardholderSearchChanged':
      'issuanceIntentId hasSearch matchCount loadedCount complete settlementReason searchAttemptId',
    'apCards.issue.cardholderLoadObserved':
      'issuanceIntentId queryAttemptId requestId state loadedCount pageCount hasMore errorId errorCode',
    'apCards.issue.cardholderSelected':
      'issuanceIntentId cardholderId previousCardholderId selectionKind formRevisionId complete',
    'apCards.issue.cardholderCreateClicked':
      'issuanceIntentId cardholderDraftId navigationAttemptId complete hasSearch hasMatches target',
    'apCards.issue.cardholderDraftApplied':
      'issuanceIntentId cardholderDraftId childScreenVisitId originatingActionAttemptId formRevisionId selectionKind persisted',
    'apCards.issue.fundingMenuChanged':
      'issuanceIntentId state reason loadedCount eligibleCount selectedFundingAccountId',
    'apCards.issue.fundingSelected':
      'issuanceIntentId previousFundingAccountId fundingAccountId eligibility formRevisionId',
    'apCards.issue.retryClicked':
      'issuanceIntentId target retryOfQueryAttemptId retryOfActionAttemptId operationId',
    'apCards.issue.validationShown':
      'issuanceIntentId fieldIds validationCodes trigger formRevisionId dispatched',
    'apCards.issue.submitClicked':
      'issuanceIntentId operationId submittedRevisionId usageType holderKind cardholderId cardholderDraftId fundingAccountId nicknamePresent amountValid',
    'apCards.issue.requestDispatched':
      'issuanceIntentId operationId submittedRevisionId requestId transportAttemptNumber recoveryRetry',
    'apCards.issue.submissionObserved':
      'issuanceIntentId operationId requestId submittedRevisionId outcome cardId status durationMs errorId errorCode',
    'apCards.issue.leaveDecision':
      'issuanceIntentId control dirty pendingOperation decision operationId target',
    'apCards.issue.destinationObserved':
      'navigationAttemptId originatingActionAttemptId issuanceIntentId cardId reason outcome targetScreenVisitId durationMs',
    'apCards.issue.returned':
      'priorScreenVisitId issuanceIntentId resumedOperationId source sameIntent sameVisit',
    'apCards.issue.supportClicked': 'issuanceIntentId placement destination'
  },
  enums: {
    visitType: ['first', 'return'],
    entrySource: ['cards_listing', 'direct', 'return'],
    usageType: ['single_use', 'multiple_use', 'unknown'],
    previousUsageType: ['single_use', 'multiple_use', 'unknown'],
    availableControlIds: [
      'nickname',
      'cardholder',
      'usage',
      'amount',
      'funding',
      'funding_retry',
      'cancel',
      'submit'
    ],
    fundingState: ['ready', 'empty', 'error'],
    cardholderLoadState: ['loading', 'complete', 'partial', 'error'],
    fieldId: ['nickname', 'amount', 'cardholder'],
    fieldIds: ['nickname', 'amount', 'cardholder', 'funding'],
    interaction: ['focus', 'blur', 'changed'],
    validationState: ['empty', 'valid', 'invalid'],
    state: [
      'open',
      'closed',
      'loading',
      'partial',
      'complete',
      'error',
      'unknown'
    ],
    reason: [
      'activation',
      'selection',
      'create',
      'escape',
      'blur',
      'outside',
      'accepted',
      'cancel',
      'discard'
    ],
    selectionKind: ['none', 'existing', 'local_new'],
    settlementReason: ['blur', 'selection', 'create', 'close'],
    holderKind: ['existing', 'local_new'],
    eligibility: ['eligible', 'disconnected', 'no_ach', 'unknown'],
    target: [
      'cardholder_dialog',
      'cardholders',
      'funding',
      'issuance',
      'cards_listing'
    ],
    validationCodes: [
      'required',
      'invalid_format',
      'nonpositive',
      'too_long',
      'out_of_range',
      'unknown'
    ],
    trigger: ['blur', 'edit', 'submit'],
    outcome: ['accepted', 'rejected', 'failed', 'unknown', 'ready'],
    status: cardStatuses,
    control: [
      'cancel',
      'discard',
      'continue_creating',
      'leave',
      'stay',
      'dismiss_guard'
    ],
    decision: ['prompted', 'leave', 'stay'],
    source: ['cardholder_dialog', 'listing', 'direct'],
    placement: ['widget'],
    destination: ['zendesk'],
    errorCode: [
      'ineligible_funding_account',
      'idempotency_conflict',
      'dispatch_failed',
      'unknown'
    ]
  }
})

export const cardStatus = (status: unknown) =>
  typeof status === 'string' &&
  (cardStatuses as readonly string[]).includes(status)
    ? status
    : 'unknown'

let supportListener: (() => void) | undefined

export const bindIssueSupport = (listener: () => void) => {
  supportListener = listener
  return () => {
    if (supportListener === listener) supportListener = undefined
  }
}

export const notifyIssueSupportOpen = () => {
  try {
    supportListener?.()
  } catch {
    /* Opening support must not fail. */
  }
}

export const observeIssueDestination = (
  accountId: string,
  listing: ApContext | undefined
) => {
  if (!listing) return
  const handoff = takeApHandoff(ISSUE_DESTINATION_HANDOFF, accountId)
  if (!handoff) return
  apAnalytics.emit(
    'apCards.issue.destinationObserved',
    {
      ...handoff.properties,
      outcome: 'ready',
      targetScreenVisitId: listing.screenVisitId,
      durationMs: handoff.elapsedMs
    },
    handoff.attempt
  )
}

export type CardholderFieldSignal =
  | {
      type: 'menu'
      open: boolean
      reason: 'activation' | 'selection' | 'create' | 'escape' | 'blur'
      loaded: number
      complete: boolean
      options: number
      selectedId: string | null
      kind: 'none' | 'existing' | 'local_new'
    }
  | {
      type: 'search'
      hasSearch: boolean
      matchCount: number
      loaded: number
      complete: boolean
      reason: 'blur' | 'selection' | 'create' | 'close'
      token: number
    }
  | {
      type: 'load'
      state: 'loading' | 'partial' | 'complete' | 'error' | 'unknown'
      loaded: number
      pages: number
      hasMore: boolean
      error?: unknown
    }
  | { type: 'select'; id: string; previousId: string | null; complete: boolean }
  | { type: 'create'; hasSearch: boolean; hasMatches: boolean }
  | { type: 'retry' }
  | {
      type: 'field'
      interaction: 'focus' | 'blur' | 'changed'
      hasValue: boolean
      validationState: 'empty' | 'valid' | 'invalid'
    }
